import { faChevronRight } from "@fortawesome/pro-light-svg-icons"
import {
  Button,
  ChatIcon,
  FlexBox,
  HandHoldingHeartIcon,
  InfoIcon,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
  VideoIcon,
} from "@joonapp/web-shared"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { PlanTitle } from "./coachingFlow/planComponents/PlanTitle"
import useIsFromMOF from "./useIsFromMOF"
import useKeepTryingToLoadCoach from "./useKeepTryingToLoadCoach"
import ListCard from "../../components/coaching/ListCard"
import PageLoader from "../../components/loading/PageLoader"
import {
  PhoneNumberModalType,
  useAddPhoneNumberModalStore,
} from "../../components/modals/addPhoneNumberModal/useAddPhoneNumberModalStore"
import { COACH_PLANS, CoachPaywallChoice } from "../../constants"
import { useGetCoachSubscriptionQuery } from "../../networking/coaching/queries"
import { useUserQuery } from "../../networking/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"

const ScheduledPage = () => {
  const { t } = useTranslation()
  const { isLoading, firstCoach } = useKeepTryingToLoadCoach()
  const router = useRouter()
  const isFromMOF = useIsFromMOF()
  const { onOpen: openAddPhoneNumberModal } = useAddPhoneNumberModalStore()
  const { data: coachSubscription } = useGetCoachSubscriptionQuery()
  const { user } = useUserQuery()
  const selectedPackage = coachSubscription?.price_lookup_key

  const planInfo = useMemo(() => {
    if (selectedPackage) {
      return COACH_PLANS[selectedPackage]
    }
    return { plan: "", frequency: null }
  }, [selectedPackage])
  const { plan, frequency } = planInfo

  useEffect(() => {
    const bundleJoonity =
      localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
        CoachPaywallChoice.WITH_COACH ||
      localStorage.getItem(localStorageItems.planChoiceFromPaywall) ===
        CoachPaywallChoice.EXIT_WITH_COACH

    if (selectedPackage && firstCoach) {
      trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_COACH_PURCHASE_COMPLETE, {
        is_tof: !isFromMOF,
        lookup_key: selectedPackage,
        bundle_joonity: bundleJoonity,
        coach_id: firstCoach?.user.id,
      })
    }
  }, [selectedPackage, isFromMOF, firstCoach])

  const onFinishClick = () => {
    router.push("/coach")
    trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_COACH_ONBOARDING_COMPLETE)
    if (!user?.phone_number) {
      openAddPhoneNumberModal(PhoneNumberModalType.COACH)
    }
  }

  if (isLoading) return <PageLoader />

  return (
    <>
      <FlexBox
        justify="flex-start"
        align="center"
        direction="column"
        fullWidth
        gap={SPACING.space4}
        style={{
          minHeight: "70vh",
          paddingTop: "20px",
          opacity: isLoading ? 0 : 1,
          pointerEvents: isLoading ? "none" : "auto",
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        <FlexBox
          direction="column"
          align="center"
          justify="center"
          gap={SPACING.space4}
          style={{ marginTop: SPACING.space6 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: SPACING.space4,
              backgroundColor: JoonColorExpanded.viridian100,
              width: "62px",
              height: "62px",
              transform: "rotate(15deg)",
              paddingBottom: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transform: "rotate(-15deg)",
              }}
            >
              <HandHoldingHeartIcon
                size={48}
                color={JoonUIColor.semantic.success}
              />
            </div>
          </div>
          <Typography variant="h2" color={JoonUIColor.text.primary}>
            {t("COACHING_SCHEDULED_STARTED")}
          </Typography>
        </FlexBox>

        <Typography variant="body" color={JoonUIColor.text.primary}>
          {t("COACHING_SCHEDULED_SUMMARY")}
        </Typography>
        <FlexBox
          direction="column"
          justify="center"
          align="center"
          style={{
            position: "relative",
            width: "min(450px, 100%)",
            background: "#F9FAFC",
            border: `2px solid ${JoonUIColor.text.primaryAccent}`,
            borderRadius: SPACING.space2,
            marginBottom: SPACING.space2,
            boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.10)",
            textAlign: "left",
            padding: SPACING.space2,
          }}
        >
          <FlexBox
            justify="left"
            align="center"
            gap={SPACING.space1}
            style={{
              marginTop: SPACING.space2,
            }}
          >
            {selectedPackage && (
              <PlanTitle plan={plan} coachingPlan={selectedPackage} />
            )}
          </FlexBox>

          <FlexBox justify="center" align="center" gap={SPACING.space6}>
            <FlexBox direction="column" align="flex-start" gap={SPACING.space0}>
              <FlexBox
                direction="row"
                justify="flex-start"
                align="center"
                gap={SPACING.space2}
                wrap={false}
                style={{ padding: SPACING.space2 }}
              >
                <div
                  style={{
                    minWidth: "20px",
                    maxWidth: "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <HandHoldingHeartIcon
                    size={20}
                    color={JoonUIColor.icon.accent}
                  />
                </div>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.primary}
                >
                  {t("COACHING_SCHEDULED_BENEFIT_COACH")}
                </Typography>
              </FlexBox>

              <FlexBox
                direction="row"
                justify="flex-start"
                align="center"
                gap={SPACING.space2}
                wrap={false}
                style={{ padding: SPACING.space2 }}
              >
                <div
                  style={{
                    minWidth: "20px",
                    maxWidth: "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ChatIcon size={20} color={JoonUIColor.icon.accent} />
                </div>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.primary}
                >
                  {t("PAYWALL_BUNDLING_MESSAGING")}
                </Typography>
              </FlexBox>

              {frequency && (
                <FlexBox
                  direction="row"
                  justify="flex-start"
                  align="center"
                  gap={SPACING.space2}
                  wrap={false}
                  style={{
                    padding: SPACING.space2,
                    paddingRight: SPACING.space0,
                    borderRadius: SPACING.space2,
                  }}
                >
                  <div
                    style={{
                      minWidth: "20px",
                      maxWidth: "20px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <VideoIcon size={20} color={JoonUIColor.icon.accent} />
                  </div>
                  <Typography
                    variant="bodyXSmallBold"
                    color={JoonUIColor.text.primary}
                  >
                    {frequency}
                  </Typography>
                </FlexBox>
              )}
            </FlexBox>
          </FlexBox>
        </FlexBox>

        <ListCard
          icon={InfoIcon}
          text={t("COACHING_SCHEDULED_ALL_SET", {
            name: firstCoach?.user.name || t("COACHING_PLAN_YOUR_COACH"),
          })}
          backgroundColor={JoonUIColor.background.lightAccent}
          gap={SPACING.space2}
          iconColor={JoonUIColor.border.accent}
        />

        <Button
          fullWidth
          onClick={onFinishClick}
          text={t("ONBOARDING_CONTINUE")}
          rightIcon={faChevronRight}
        />
      </FlexBox>
    </>
  )
}

export default ScheduledPage
